/**
 * Configuration complète des ressources AWS
 * Définit les champs requis, optionnels et les suggestions pour chaque type de ressource
 */

export interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'boolean' | 'textarea';
  required: boolean;
  default?: any;
  options?: string[];
  suggestions?: string[];
  placeholder?: string;
  description?: string;
  validation?: (value: any) => string | null;
}

export interface ResourceConfig {
  displayName: string;
  category: string;
  description: string;
  fields: FieldConfig[];
  priorityFields?: string[]; // Champs à afficher en priorité (mode simple)
  relationshipSuggestions?: {
    inbound?: string[];
    outbound?: string[];
  };
}

// Validation functions
const containsVariable = (value: string): boolean => /\{\{[^}]+\}\}/.test(value);

const validateName = (value: string): string | null => {
  if (!value) return "Le nom est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-zA-Z0-9_-]{1,128}$/.test(value)) {
    return "Nom invalide: utiliser lettres, chiffres, tirets et underscores (1-128 caractères)";
  }
  return null;
};

const validateBucketName = (value: string): string | null => {
  if (!value) return "Le nom est requis";
  if (containsVariable(value)) return null;
  if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(value)) {
    return "Nom invalide: 3-63 caractères alphanumériques en minuscules, points et tirets";
  }
  return null;
};

const validateRegion = (value: string): string | null => {
  // Format AWS region : xx-xxxx-N (ex: eu-west-3, ap-southeast-1, us-east-1)
  if (value && !/^[a-z]{2}-[a-z]+-\d$/.test(value)) {
    return "Région AWS non valide (ex: eu-west-3, us-east-1)";
  }
  return null;
};

const validateCIDR = (value: string): string | null => {
  if (!value) return null;
  if (!/^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/.test(value)) {
    return "Format CIDR invalide (ex: 10.0.0.0/16)";
  }
  return null;
};

export const AWS_RESOURCE_CONFIG: Record<string, ResourceConfig> = {
  'aws_vpc': {
    displayName: 'VPC',
    category: 'Network',
    description: 'Virtual Private Cloud pour isoler vos ressources AWS',
    fields: [
      {
        key: 'cidr_block',
        label: 'CIDR Block',
        type: 'text',
        required: true,
        default: '10.0.0.0/16',
        placeholder: '10.0.0.0/16',
        suggestions: ['10.0.0.0/16', '172.16.0.0/12', '192.168.0.0/16'],
        description: 'Plage d\'adresses IP CIDR pour le VPC',
        validation: validateCIDR,
      },
      {
        key: 'enable_dns_hostnames',
        label: 'DNS Hostnames',
        type: 'boolean',
        required: false,
        default: true,
        description: 'Activer les noms d\'hôte DNS',
      },
      {
        key: 'enable_dns_support',
        label: 'DNS Support',
        type: 'boolean',
        required: false,
        default: true,
        description: 'Activer le support DNS',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "main-vpc", "Environment": "production"}',
        description: 'Tags pour organiser les ressources (JSON)',
      },
    ],
    relationshipSuggestions: {
      outbound: ['aws_subnet', 'aws_internet_gateway', 'aws_security_group'],
    },
  },

  'aws_subnet': {
    displayName: 'Subnet',
    category: 'Network',
    description: 'Sous-réseau à l\'intérieur d\'un VPC',
    fields: [
      {
        key: 'vpc_id',
        label: 'VPC ID',
        type: 'text',
        required: true,
        description: 'ID du VPC parent',
        placeholder: 'vpc-xxxxx',
      },
      {
        key: 'cidr_block',
        label: 'CIDR Block',
        type: 'text',
        required: true,
        default: '10.0.1.0/24',
        suggestions: ['10.0.1.0/24', '10.0.2.0/24', '10.0.3.0/24'],
        description: 'Plage CIDR pour ce subnet (doit être dans le VPC)',
        validation: validateCIDR,
      },
      {
        key: 'availability_zone',
        label: 'Availability Zone',
        type: 'text',
        required: false,
        placeholder: 'us-east-1a',
        suggestions: ['us-east-1a', 'us-east-1b', 'us-east-1c'],
        description: 'Zone de disponibilité AWS',
      },
      {
        key: 'map_public_ip_on_launch',
        label: 'Public IP on Launch',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Attribuer une IP publique aux instances lancées',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "subnet-web", "Tier": "public"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_vpc'],
      outbound: ['aws_instance', 'aws_db_instance', 'aws_lb'],
    },
  },

  'aws_s3_bucket': {
    displayName: 'S3 Bucket',
    category: 'Storage',
    description: 'Stockage d\'objets scalable et durable',
    fields: [
      {
        key: 'bucket',
        label: 'Nom du bucket',
        type: 'text',
        required: true,
        placeholder: 'my-app-bucket',
        suggestions: ['app-data-bucket', 'backup-bucket', 'static-assets'],
        validation: validateBucketName,
      },
      {
        key: 'storage_class',
        label: 'Classe de stockage',
        type: 'select',
        required: false,
        default: 'Standard',
        options: ['Standard', 'Standard-IA', 'Glacier', 'Glacier Deep Archive'],
        description: 'Classe de stockage (Standard: $0.023/GB, IA: $0.0125/GB, Glacier: $0.004/GB)',
      },
      {
        key: 'capacity_gb',
        label: 'Capacité estimée (GB)',
        type: 'number',
        required: false,
        default: 1,
        placeholder: '1',
        description: 'Taille de stockage estimée en GB pour le calcul des coûts',
      },
      {
        key: 'acl',
        label: 'ACL',
        type: 'select',
        required: false,
        default: 'private',
        options: ['private', 'public-read', 'public-read-write', 'authenticated-read'],
        description: 'Liste de contrôle d\'accès',
      },
      {
        key: 'versioning',
        label: 'Versioning',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Activer le versioning des objets',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Purpose": "storage"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_instance', 'aws_lambda_function', 'aws_cloudfront_distribution'],
    },
  },

  'aws_instance': {
    displayName: 'EC2 Instance',
    category: 'Compute',
    description: 'Machine virtuelle pour exécuter des applications',
    fields: [
      {
        key: 'ami',
        label: 'AMI',
        type: 'text',
        required: true,
        placeholder: 'ami-xxxxx',
        suggestions: ['ami-0c55b159cbfafe1f0', 'ami-0d5d9d301c853a04a'],
        description: 'ID de l\'image Amazon Machine Image',
      },
      {
        key: 'instance_type',
        label: 'Type d\'instance',
        type: 'select',
        required: true,
        default: 't3.micro',
        options: [
          't3.micro', 't3.small', 't3.medium', 't3.large',
          't2.micro', 't2.small', 't2.medium',
          'm5.large', 'm5.xlarge', 'm5.2xlarge',
          'c5.large', 'c5.xlarge', 'r5.large'
        ],
        description: 'Taille et capacité de l\'instance',
      },
      {
        key: 'subnet_id',
        label: 'Subnet ID',
        type: 'text',
        required: false,
        placeholder: 'subnet-xxxxx',
        description: 'ID du subnet où lancer l\'instance',
      },
      {
        key: 'key_name',
        label: 'Key Pair',
        type: 'text',
        required: false,
        placeholder: 'my-key-pair',
        description: 'Nom de la paire de clés SSH',
      },
      {
        key: 'monitoring',
        label: 'Monitoring détaillé',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Activer CloudWatch monitoring détaillé',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "web-server", "Environment": "production"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_subnet', 'aws_security_group', 'aws_lb'],
      outbound: ['aws_ebs_volume', 'aws_s3_bucket'],
    },
  },

  'aws_elastic_beanstalk_application': {
    displayName: 'Elastic Beanstalk App',
    category: 'Web',
    description: 'Plateforme managée pour déployer et scaler des applications web',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'my-web-app',
        suggestions: ['webapp', 'api-service', 'frontend-app'],
        validation: validateName,
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Mon application web',
        description: 'Description de l\'application',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Team": "DevOps"}',
      },
    ],
    relationshipSuggestions: {
      outbound: ['aws_elastic_beanstalk_environment', 'aws_db_instance', 'aws_s3_bucket'],
    },
  },

  'aws_db_instance': {
    displayName: 'RDS Instance',
    category: 'Database',
    description: 'Base de données relationnelle managée',
    fields: [
      {
        key: 'identifier',
        label: 'Identifiant',
        type: 'text',
        required: true,
        placeholder: 'mydb',
        suggestions: ['prod-db', 'app-database', 'mysql-db'],
        validation: validateName,
      },
      {
        key: 'engine',
        label: 'Moteur',
        type: 'select',
        required: true,
        default: 'mysql',
        options: [
          'mysql', 'postgres', 'mariadb', 'oracle-se2',
          'sqlserver-ex', 'sqlserver-web', 'sqlserver-se', 'sqlserver-ee'
        ],
        description: 'Moteur de base de données (se=Standard, ee=Enterprise)',
      },
      {
        key: 'engine_version',
        label: 'Version',
        type: 'text',
        required: false,
        placeholder: '8.0',
        suggestions: ['8.0', '5.7', '14.1'],
      },
      {
        key: 'instance_class',
        label: 'Classe d\'instance',
        type: 'select',
        required: true,
        default: 'db.t3.micro',
        options: [
          'db.t3.micro', 'db.t3.small', 'db.t3.medium', 'db.t3.large', 'db.t3.xlarge', 'db.t3.2xlarge',
          'db.m5.large', 'db.m5.xlarge', 'db.m5.2xlarge', 'db.m5.4xlarge',
          'db.r5.large', 'db.r5.xlarge', 'db.r5.2xlarge'
        ],
        description: 'Taille de l\'instance RDS',
      },
      {
        key: 'allocated_storage',
        label: 'Stockage (GB)',
        type: 'number',
        required: true,
        default: 20,
        description: 'Taille du stockage en GB',
      },
      {
        key: 'username',
        label: 'Utilisateur admin',
        type: 'text',
        required: true,
        placeholder: 'admin',
        suggestions: ['admin', 'root', 'dbadmin'],
      },
      {
        key: 'multi_az',
        label: 'Multi-AZ',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Déploiement Multi-AZ pour haute disponibilité',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "production-db", "Environment": "prod"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_subnet', 'aws_security_group', 'aws_instance'],
      outbound: ['aws_db_subnet_group'],
    },
  },

  'aws_lb': {
    displayName: 'Load Balancer',
    category: 'Network',
    description: 'Application Load Balancer ou Network Load Balancer',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'app-lb',
        suggestions: ['web-lb', 'api-lb', 'internal-lb'],
        validation: validateName,
      },
      {
        key: 'load_balancer_type',
        label: 'Type',
        type: 'select',
        required: false,
        default: 'application',
        options: ['application', 'network', 'gateway'],
        description: 'Type de load balancer',
      },
      {
        key: 'internal',
        label: 'Internal',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Load balancer interne (non accessible depuis Internet)',
      },
      {
        key: 'subnets',
        label: 'Subnets',
        type: 'textarea',
        required: true,
        placeholder: '["subnet-xxxxx", "subnet-yyyyy"]',
        description: 'Liste des IDs de subnets (JSON array)',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "app-lb", "Environment": "production"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_subnet', 'aws_security_group'],
      outbound: ['aws_instance', 'aws_lb_target_group'],
    },
  },

  'aws_eks_cluster': {
    displayName: 'EKS Cluster',
    category: 'Compute',
    description: 'Cluster Kubernetes managé AWS',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'my-eks-cluster',
        suggestions: ['prod-eks', 'dev-eks', 'k8s-cluster'],
        validation: validateName,
      },
      {
        key: 'role_arn',
        label: 'Role ARN',
        type: 'text',
        required: true,
        placeholder: 'arn:aws:iam::123456789:role/eks-cluster-role',
        description: 'ARN du rôle IAM pour le cluster',
      },
      {
        key: 'version',
        label: 'Version Kubernetes',
        type: 'select',
        required: false,
        default: '1.28',
        options: ['1.28', '1.27', '1.26', '1.25'],
      },
      {
        key: 'subnet_ids',
        label: 'Subnet IDs',
        type: 'textarea',
        required: true,
        placeholder: '["subnet-xxxxx", "subnet-yyyyy"]',
        description: 'Liste des IDs de subnets (JSON array)',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Team": "Platform"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_subnet', 'aws_iam_role'],
      outbound: ['aws_eks_node_group', 'aws_ecr_repository'],
    },
  },

  'aws_secretsmanager_secret': {
    displayName: 'Secrets Manager',
    category: 'Security',
    description: 'Gestionnaire de secrets pour stocker et gérer les informations sensibles',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'prod/db/password',
        suggestions: ['prod/api/key', 'app/db/credentials', 'service/token'],
        validation: validateName,
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        required: false,
        placeholder: 'Mot de passe de la base de données production',
      },
      {
        key: 'recovery_window_in_days',
        label: 'Fenêtre de récupération (jours)',
        type: 'number',
        required: false,
        default: 30,
        description: 'Nombre de jours avant suppression définitive (0 ou 7-30)',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Managed": "terraform"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_instance', 'aws_lambda_function', 'aws_ecs_task_definition'],
    },
  },

  'aws_ecr_repository': {
    displayName: 'ECR Repository',
    category: 'Containers',
    description: 'Registre privé pour images Docker',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'my-app',
        suggestions: ['webapp', 'api-service', 'worker'],
        validation: validateName,
      },
      {
        key: 'image_tag_mutability',
        label: 'Mutabilité des tags',
        type: 'select',
        required: false,
        default: 'MUTABLE',
        options: ['MUTABLE', 'IMMUTABLE'],
        description: 'Permet ou non de remplacer les tags existants',
      },
      {
        key: 'scan_on_push',
        label: 'Scan on Push',
        type: 'boolean',
        required: false,
        default: true,
        description: 'Scanner les images lors du push',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Team": "DevOps"}',
      },
    ],
    relationshipSuggestions: {
      outbound: ['aws_eks_cluster', 'aws_ecs_task_definition'],
    },
  },

  'aws_security_group': {
    displayName: 'Security Group',
    category: 'Security',
    description: 'Pare-feu virtuel pour contrôler le trafic réseau',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'web-sg',
        suggestions: ['web-sg', 'app-sg', 'db-sg'],
        validation: validateName,
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        required: false,
        placeholder: 'Security group for web servers',
      },
      {
        key: 'vpc_id',
        label: 'VPC ID',
        type: 'text',
        required: true,
        placeholder: 'vpc-xxxxx',
        description: 'ID du VPC',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Name": "web-sg", "Environment": "production"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_vpc'],
      outbound: ['aws_instance', 'aws_lb', 'aws_db_instance'],
    },
  },

  'aws_iam_role': {
    displayName: 'IAM Role',
    category: 'Security',
    description: 'Rôle IAM pour déléguer des permissions',
    fields: [
      {
        key: 'name',
        label: 'Nom',
        type: 'text',
        required: true,
        placeholder: 'app-role',
        suggestions: ['ec2-role', 'lambda-role', 'eks-role'],
        validation: validateName,
      },
      {
        key: 'assume_role_policy',
        label: 'Assume Role Policy',
        type: 'textarea',
        required: true,
        placeholder: '{\n  "Version": "2012-10-17",\n  "Statement": []\n}',
        description: 'Policy JSON définissant qui peut assumer ce rôle',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        required: false,
        placeholder: 'Role for EC2 instances',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Managed": "terraform"}',
      },
    ],
    relationshipSuggestions: {
      outbound: ['aws_instance', 'aws_lambda_function', 'aws_eks_cluster'],
    },
  },

  'aws_lambda_function': {
    displayName: 'Lambda Function',
    category: 'Compute',
    description: 'Fonction serverless pour exécuter du code sans gérer de serveurs',
    priorityFields: ['function_name', 'runtime', 'handler', 'role', 'filename', 'source_code_hash'],
    fields: [
      {
        key: 'function_name',
        label: 'Nom de la fonction',
        type: 'text',
        required: true,
        placeholder: 'my-function',
        suggestions: ['api-handler', 'data-processor', 'event-handler'],
        validation: validateName,
      },
      {
        key: 'runtime',
        label: 'Runtime',
        type: 'select',
        required: true,
        default: 'python3.11',
        options: [
          'python3.11', 'python3.10', 'python3.9',
          'nodejs20.x', 'nodejs18.x', 'nodejs16.x',
          'java17', 'java11', 'java8.al2',
          'dotnet8', 'dotnet6',
          'go1.x',
          'ruby3.2', 'ruby2.7',
          'provided.al2023', 'provided.al2'
        ],
        description: 'Environnement d\'exécution',
      },
      {
        key: 'handler',
        label: 'Handler',
        type: 'text',
        required: true,
        placeholder: 'index.handler',
        suggestions: ['index.handler', 'lambda_function.lambda_handler', 'main.handler'],
        description: 'Point d\'entrée de la fonction (fichier.fonction)',
      },
      {
        key: 'role',
        label: 'IAM Role ARN',
        type: 'text',
        required: true,
        placeholder: 'arn:aws:iam::123456789:role/lambda-role',
        description: 'ARN du rôle IAM pour la fonction',
      },
      {
        key: 'filename',
        label: 'Fichier ZIP',
        type: 'text',
        required: false,
        placeholder: 'function.zip',
        description: 'Chemin vers le fichier ZIP du code',
      },
      {
        key: 's3_bucket',
        label: 'S3 Bucket',
        type: 'text',
        required: false,
        placeholder: 'my-lambda-bucket',
        description: 'Bucket S3 contenant le code (alternative à filename)',
      },
      {
        key: 's3_key',
        label: 'S3 Key',
        type: 'text',
        required: false,
        placeholder: 'lambda/function.zip',
        description: 'Clé S3 du fichier ZIP',
      },
      {
        key: 'source_code_hash',
        label: 'Source Code Hash',
        type: 'text',
        required: false,
        placeholder: 'filebase64sha256("function.zip")',
        description: 'Hash du code source pour déclencher les mises à jour',
      },
      {
        key: 'memory_size',
        label: 'Mémoire (MB)',
        type: 'number',
        required: false,
        default: 128,
        description: 'Mémoire allouée (128-10240 MB)',
      },
      {
        key: 'timeout',
        label: 'Timeout (secondes)',
        type: 'number',
        required: false,
        default: 3,
        description: 'Durée maximale d\'exécution (1-900 secondes)',
      },
      {
        key: 'environment',
        label: 'Variables d\'environnement',
        type: 'textarea',
        required: false,
        placeholder: '{"variables": {"ENV": "production", "DB_HOST": "..."}}',
        description: 'Variables d\'environnement (JSON)',
      },
      {
        key: 'layers',
        label: 'Layers ARN',
        type: 'textarea',
        required: false,
        placeholder: '["arn:aws:lambda:us-east-1:123:layer:my-layer:1"]',
        description: 'Liste des ARN de layers (JSON array)',
      },
      {
        key: 'reserved_concurrent_executions',
        label: 'Exécutions concurrentes réservées',
        type: 'number',
        required: false,
        description: 'Nombre d\'exécutions concurrentes réservées (-1 = illimité)',
      },
      {
        key: 'publish',
        label: 'Publier version',
        type: 'boolean',
        required: false,
        default: false,
        description: 'Publier une nouvelle version à chaque déploiement',
      },
      {
        key: 'vpc_config',
        label: 'Configuration VPC',
        type: 'textarea',
        required: false,
        placeholder: '{"subnet_ids": ["subnet-xxx"], "security_group_ids": ["sg-xxx"]}',
        description: 'Configuration VPC (JSON)',
      },
      {
        key: 'dead_letter_config',
        label: 'Dead Letter Queue',
        type: 'textarea',
        required: false,
        placeholder: '{"target_arn": "arn:aws:sqs:..."}',
        description: 'Configuration DLQ pour les échecs (JSON)',
      },
      {
        key: 'tracing_config',
        label: 'Configuration de tracing',
        type: 'textarea',
        required: false,
        placeholder: '{"mode": "Active"}',
        description: 'Configuration AWS X-Ray (JSON)',
      },
      {
        key: 'tags',
        label: 'Tags',
        type: 'textarea',
        required: false,
        placeholder: '{"Environment": "production", "Function": "api-handler"}',
      },
    ],
    relationshipSuggestions: {
      inbound: ['aws_iam_role', 'aws_s3_bucket', 'aws_subnet', 'aws_security_group'],
      outbound: ['aws_dynamodb_table', 'aws_sqs_queue', 'aws_sns_topic'],
    },
  },
};

/**
 * Obtenir la configuration d'une ressource par son type Terraform
 */
export const getResourceConfig = (tfKey: string): ResourceConfig | null => {
  return AWS_RESOURCE_CONFIG[tfKey] || null;
};

/**
 * Obtenir les suggestions pour un champ spécifique
 */
export const getFieldSuggestions = (tfKey: string, fieldKey: string): string[] => {
  const config = getResourceConfig(tfKey);
  if (!config) return [];

  const field = config.fields.find(f => f.key === fieldKey);
  return field?.suggestions || field?.options || [];
};
